import { useAnimationControls } from "framer-motion";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import type { RemovalDetails } from "../../../hooks/useHandleNurseChange";
import { AbsenceType } from "../../../utils/models";
import { useCallInReasonOptions } from "../../team/CallInReasons/callInReasonsHooks";
import { ATTENTION_SHAKE } from "../../UIToolkit/Animations/attentionShake";
import { useRemovalReasonState } from "../hooks/useRemovalReasonState";

/**
 * What the manager records about the removal — one reason, note and call-in time for every shift picked. The
 * reason is required in every department (hospitals included) and nothing is pre-selected.
 */
export const useRemoveStaffDetails = () => {
	const reason = useRemovalReasonState({ hospitalRecordsCallIn: false });
	const { selectedReason, isReasonValid, getFullReason, removalMessage, handleReasonChange } = reason;
	const reasonOptions = useCallInReasonOptions();
	const [callInTime, setCallInTime] = useState<DateTime>(() => DateTime.now());
	const [showReasonError, setShowReasonError] = useState(false);
	const shakeControls = useAnimationControls();
	const isCallInReason = selectedReason === AbsenceType.CALL_IN;

	const changeReason = useCallback(
		(value: string | undefined) => {
			handleReasonChange(value);
			if (value) setShowReasonError(false);
		},
		[handleReasonChange],
	);

	/** True when the removal may go ahead; otherwise flags the reason field and shakes it. */
	const confirmReason = useCallback(async (): Promise<boolean> => {
		if (isReasonValid) return true;
		setShowReasonError(true);
		await shakeControls.start(ATTENTION_SHAKE);
		return false;
	}, [isReasonValid, shakeControls]);

	const toRemovalDetails = useCallback(
		(): RemovalDetails => ({
			message: getFullReason(),
			// DEFAULT: an empty note field is no note — none is sent, rather than an empty string saved with the removal.
			removalMessage: removalMessage || undefined,
			callInTime: isCallInReason ? callInTime : undefined,
		}),
		[getFullReason, removalMessage, isCallInReason, callInTime],
	);

	return {
		...reason,
		changeReason,
		reasonOptions,
		isCallInReason,
		callInTime,
		setCallInTime,
		showReasonError,
		shakeControls,
		confirmReason,
		toRemovalDetails,
	};
};

export type RemoveStaffDetails = ReturnType<typeof useRemoveStaffDetails>;
